import * as React from "react";
import { Form, Formik, FormikHelpers, FormikProps } from "formik";
import { default as dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { Box, Button, Grid, Stack } from "@mui/material";
import { DATE_PICKER_FORMAT, DatePickerField } from "components/formikFields/DatePickerField/DatePickerField";
import { SelectOption } from "@mui/base";
import { ClientCompanySelect } from "../../MachineTranslationAnalyticsPage/components/ClientCompanySelect";

interface Props {
  from: Date;
  to: Date;
  client: SelectOption<string>;
  isSearching: boolean;
  onChange: (from: Date, to: Date, client: SelectOption<string>) => void;
}

enum FieldNames {
  client = "client",
  from = "from",
  to = "to"
}

interface Values {
  [FieldNames.client]: SelectOption<string>;
  [FieldNames.from]: string;
  [FieldNames.to]: string;
}

dayjs.extend(utc);
dayjs.extend(timezone);

const Filter: React.FC<Props> = ({ from, to, client, isSearching, onChange }) => {
  const formikRef = React.useRef<FormikProps<Values>>(null);

  const initialValue = React.useMemo(
    () => ({
      client,
      from: dayjs(from).format(DATE_PICKER_FORMAT),
      to: dayjs(to).format(DATE_PICKER_FORMAT)
    }),
    [client, from, to]
  );

  const validate = React.useCallback((values: Values) => {
    const from = values[FieldNames.from];
    const to = values[FieldNames.to];
    const fromValue = dayjs(from, DATE_PICKER_FORMAT);
    const toValue = dayjs(to, DATE_PICKER_FORMAT);
    const errors: Record<string, string> = {};

    if (!values[FieldNames.client]?.value) {
      errors[FieldNames.client] = "Required";
    }

    if (!from) {
      errors[FieldNames.from] = "Required";
    }

    if (!to) {
      errors[FieldNames.to] = "Required";
    }

    if (from && to) {
      if (toValue.isBefore(fromValue)) {
        errors[FieldNames.to] = "Start date must be before end date";
      } else if (toValue.subtract(365, "day").isAfter(fromValue)) {
        errors[FieldNames.to] = "Search range cannot exceed 365 days";
      }
    }

    return errors;
  }, []);

  const executeSearch = React.useCallback(
    (values: Values) => {
      const fromValue = dayjs
        .tz(values[FieldNames.from], DATE_PICKER_FORMAT, "America/Los_Angeles")
        .startOf("day")
        .toDate();
      const toValue = dayjs.tz(values[FieldNames.to], DATE_PICKER_FORMAT, "America/Los_Angeles").endOf("day").toDate();

      onChange(fromValue, toValue, values[FieldNames.client]);
    },
    [onChange]
  );

  const onSubmit = React.useCallback(
    (values: Values, { setSubmitting }: FormikHelpers<Values>) => {
      executeSearch(values);
      setSubmitting(false);
    },
    [executeSearch]
  );

  const onClientChange = React.useCallback(
    (clientOption: SelectOption<string>) => {
      const formik = formikRef.current;

      if (!formik) {
        return;
      }

      const values: Values = {
        ...formik.values,
        [FieldNames.client]: clientOption
      };
      const errors = validate(values);

      formik.setFieldTouched(FieldNames.client, true, false);
      formik.setFieldTouched(FieldNames.from, true, false);
      formik.setFieldTouched(FieldNames.to, true, false);
      formik.setErrors(errors);

      if (Object.keys(errors).length === 0) {
        executeSearch(values);
      }
    },
    [executeSearch, validate]
  );

  return (
    <Formik
      initialValues={initialValue}
      validate={validate}
      onSubmit={onSubmit}
      enableReinitialize={true}
      innerRef={formikRef}
    >
      <Form autoComplete="off" autoCorrect="off" noValidate={true}>
        <Grid container={true} mt={1} spacing={2} alignItems="flex-start">
          <Grid item={true} xs={12} md={4}>
            <ClientCompanySelect
              name={FieldNames.client}
              onChange={onClientChange}
              variant="outlined"
              size="small"
              disabled={isSearching}
            />
          </Grid>
          <Grid item={true} xs={12} md={8}>
            <Stack direction="row" spacing={2} flexWrap="wrap">
              <DatePickerField
                name={FieldNames.from}
                label="Start date"
                required={true}
                disabled={isSearching}
                variant="outlined"
                size="small"
                disableFuture={true}
              />
              <DatePickerField
                name={FieldNames.to}
                label="End date"
                required={true}
                disabled={isSearching}
                variant="outlined"
                size="small"
                disableFuture={true}
              />
              <Box>
                <Button type="submit" variant="contained" disabled={isSearching}>
                  Search
                </Button>
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  );
};

export { Filter };
